import { useRef, useState, useEffect } from "react"
import classNames from "classnames"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import { TypingLoader } from "components/TypingLoader/TypingLoader"
import { ChatHeader, MobileHeader } from "../ChatHeader/ChatHeader"
import ChatInput from "../ChatInput/ChatInput"
import EndChatButton from "../EndChatBtn/EndChatBtn"
import { MessageBubble } from "../MessageBubble/MessageBubble"
import { ChatInstructions, MobileChatInstructions } from "../ChatInstructions/ChatInstructions"
import ChatTimeWarningProgress from "components/Chat/ChatTimeWarningProgress/ChatTimeWarningProgress"
import StartChatBtn from "../StartChatBtn/StartChatBtn"
import SubmissionStatusModal from "./SubmissionStatus"
import { GC } from "services/gamecentreService"
import { storage } from "services/storage"
import { getChatMessage, getRandomInRange } from "utils/helper"
import { API_RESPONSE_EVENT, API_SAVE_CHAT_EVENT } from "constant"
import { ChatSlash } from "assets/icons"
import "./index.css"
import { useLanguageContext } from "store/LanguageProvider"

let timerInterval, newChatTimeout
/**
 * Separate Chat component for multiple chats.
 * Reusing child components - ChatInput, MessageBubble and TypingLoader.
 * Has it's own getBotReply, optionSubmitHandler and timer logic.
 * Keeping track of id (question id) throughtout the chat flow.
 * @param {array} questions - list of all questions in quiz data.
 * @param {number} timer - quiz timer in seconds.
 * @param {string} quizId
 * @param {function} quizEndHandler - function to call when the quiz has ended
 * 
 * @property {object} allMessages - Object with question id as key and conversation array as value.
 * @property {object} currentQuestion - current question object.
 * @property {array} notificationMessages - array of question ids for which reply is fetched but user has not viewed it yet.
 * @property {array} loadingMessages - array of question ids for which reply is being fetched.
 * @property {array} toBeFetchedChatsRef - array of question ids which are queued for fetching their initial message.
 */
const MultiChatQuiz = ({
	questions = null,
	timer = null,
	quizId = "",
	quizEndHandler = () => { },
}) => {
	const [allMessages, setAllMessages] = useState({})
	const [currentQuestion, setCurrentQuestion] = useState({})
	const [notificationMessages, setNotificationMessages] = useState([])
	const [loadingMessages, setLoadingMessages] = useState([])
	const [resolvedQuestions, setResolvedQuestions] = useState([])
	const [submissionStatus, setSubmissionStatus] = useState({})
	const [isSubmissionStatusVisible, setIsSubmissionStatusVisible] = useState(false)
	const [timeLeft, setTimeLeft] = useState(timer)
	const [instructionsVisible, setInstructionsVisible] = useState(true)
	const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
	const [mobileInstructionsVisible, setMobileInstructionsVisible] = useState(false)
	const toBeFetchedChatsRef = useRef([])
	const resolvedQuestionsRef = useRef([])
	const allMessagesRef = useRef({})
	const chatMetricsRef = useRef({})
	const inputRef = useRef(null)

	const { direction, rtlView, userLanguage } = useLanguageContext()
	const { t } = useTranslation()

	const inIframe = window.location !== window.parent.location

	useEffect(() => {
		window.addEventListener(API_SAVE_CHAT_EVENT, handleChatSaveEvent)
		return () => window.removeEventListener(API_SAVE_CHAT_EVENT, handleChatSaveEvent)
	}, [])

	/**
	 * Initially, fetch conversation for first question.
	 * Start the timer.
	 */
	useEffect(() => {
		let timeout
		if (!instructionsVisible) {
			if (questions) {
				setCurrentQuestion(questions[0])
				timeout = setTimeout(() => {
					getBotReply([], questions[0].id)
				}, 0)
			}
			return () => {
				clearTimeout(timeout)
				clearInterval(timerInterval)
			}
		}
	}, [instructionsVisible])

	/**
	 * Scroll all selected containers to bottom (Desktop, Mobile)
	 */
	const scrollAllContainersToBottom = () => {
		const scrollSelectors = [
			'#chat-scroll-section',
			'.scroll-container',
		];

		scrollSelectors.forEach(selector => {
			const elements = document.querySelectorAll(selector);
			elements.forEach(element => {
				if (element && element.scrollHeight > element.clientHeight) {
					element.scrollTo({
						top: element.scrollHeight,
						behavior: "smooth"
					});
				}
			});
		});
	};

	/**
	 * Adjust scroll when a new message is sent or current question is changed.
	 */
	useEffect(() => {
		scrollAllContainersToBottom();
	}, [allMessages, currentQuestion])

	/**
	 * Set input to focus on initial render after instructions or when the loading a message has finished. 
	 */
	useEffect(() => {
		if (!loadingMessages?.includes(currentQuestion?.id) || !instructionsVisible) inputRef?.current?.focus()
	}, [currentQuestion, loadingMessages, instructionsVisible])

	/**
	 * End all chats when timer ends.
	 */
	useEffect(() => {
		if (timeLeft == 0) {
			onEndSession()
		}
	}, [timeLeft])

	/**
	 * When a bot reply is recieved, update messages for respective question and update the loading state 
	 * for that question.
	 */
	useEffect(() => {
		if (!currentQuestion) return

		const newMessageHandler = () => {
			const newMessages = storage.get.chats()
			if (newMessages && Array.isArray(newMessages)) {
				const id = newMessages[newMessages.length - 1].id

				toBeFetchedChatsRef.current = toBeFetchedChatsRef.current.filter(quesId => quesId !== id)
				chatMetricsRef.current[id] = { ...chatMetricsRef.current[id], latestBotMsgTime: new Date() }
				setLoadingMessages(prev => prev.filter(lMessage => lMessage !== id))
				updateMessages(newMessages)
			} else {
				console.error("Failure in setting messages as the data is falsy", newMessages)
			}
		}

		window.addEventListener(API_RESPONSE_EVENT, newMessageHandler)

		return () => {
			window.removeEventListener(API_RESPONSE_EVENT, newMessageHandler)
		}
	}, [currentQuestion])

	const handleChatSaveEvent = (e) => {
		const { detail: { question: { id } } } = e
		setSubmissionStatus(prev => ({ ...prev, [id]: "submitted" }))
	}

	/**
	 * Get a bot reply for a question id.
	 */
	const getBotReply = async (messages, id, replyDelay = 0) => {
		setLoadingMessages(prev => ([...prev, id]))
		const newReplyTimeout = setTimeout(async () => {
			const question = questions.find(question => question.id === id)
			const promptVariables = {
				scenario: question.question,
				skills: question?.traitNames || [],
				language: userLanguage,
				questionId: question.id,
			}
			if (inIframe) {
				GC.sendGameDataSaveMessage({
					messages,
					promptId: question.completionPrompt,
					generateChatReply: true,
					id,
					promptVariables,
				})
			} else {
				const chatResponse = await getChatMessage({
					promptId: question.completionPrompt,
					messages,
					id,
					promptVariables,
				})
				if (chatResponse) {
					storage.set.chats(chatResponse)
					window.dispatchEvent(new Event(API_RESPONSE_EVENT))
				}
			}
			clearTimeout(newReplyTimeout)
		}, replyDelay * 1000)
	}

	/**
	 * Update the chat metrics like average reply time and typing speed.
	 */
	const onTypingEnd = (questionId, wordsTyped = 0) => {
		const currentTime = new Date()
		const timeTakenInReply = (currentTime - chatMetricsRef.current?.[questionId]?.latestBotMsgTime) / 1000
		const timeTakenInTyping = (currentTime - chatMetricsRef.current?.[questionId]?.typingStartTime) / 1000
		const currentTotalReplyTime = chatMetricsRef.current?.[questionId]?.totalReplyTime ?? 0
		const currentTotalWordsTyped = chatMetricsRef.current?.[questionId]?.totalWordsTyped ?? 0
		const currentTotalTypingTime = chatMetricsRef.current?.[questionId]?.totalTypingTime ?? 0
		const userMsgsCount = (allMessagesRef.current?.[questionId]?.length + 1) / 2
		chatMetricsRef.current[questionId] = {
			...chatMetricsRef.current[questionId],
			totalReplyTime: currentTotalReplyTime + timeTakenInReply,
			totalWordsTyped: currentTotalWordsTyped + wordsTyped,
			totalTypingTime: currentTotalTypingTime + timeTakenInTyping,
			userMsgsCount: userMsgsCount,
		}
	}

	/**
	 * Update the typing start time for the question when user starts typing.
	 */
	const onTypingStart = (questionId) => {
		const currentTime = new Date()
		chatMetricsRef.current[questionId] = { ...chatMetricsRef.current[questionId], typingStartTime: currentTime }
	}

	/**
	 * Update messages for a specific question. Remove id before sending the messages in payload.
	 * Update the notification state for this question.
	 * Get bot reply for next question with a time gap.
	 */
	const updateMessages = (messages) => {
		const updatedMessages = [...messages]
		const { id, content } = updatedMessages[updatedMessages.length - 1]
		delete updatedMessages[updatedMessages.length - 1].id

		allMessagesRef.current = { ...allMessagesRef.current, [id]: updatedMessages }
		setAllMessages(allMessagesRef.current)

		if (currentQuestion.id !== id) {
			setNotificationMessages(prev => ({ ...prev, [id]: content }))
		}

		const allMessagesArrLength = Object.keys(allMessagesRef.current ?? [])?.length
		if (allMessagesArrLength < questions?.length) {
			const nextQuestionId = questions?.[allMessagesArrLength]?.id
			if (!toBeFetchedChatsRef.current?.includes(nextQuestionId)) {
				const timerVal = getRandomInRange(5, 30)
				toBeFetchedChatsRef.current = [...toBeFetchedChatsRef.current, nextQuestionId]
				newChatTimeout = setTimeout(async () => {
					getBotReply([], nextQuestionId)
					clearTimeout(newChatTimeout)
				}, timerVal * 1000)
			}
		}
	}

	/**
	 * Change current question view and update it's notification status.
	 */
	const onQuestionChange = (e, id) => {
		e?.stopPropagation()

		const newQuestion = questions.find(question => question?.id === id)
		setCurrentQuestion(newQuestion)

		const updatedNotificationMessages = { ...notificationMessages }
		delete updatedNotificationMessages[newQuestion.id]
		setNotificationMessages({ ...updatedNotificationMessages })
	}

	const optionSubmitHandler = ({
		answerText,
		finish,
		questionId,
		timeSpentOnAnswer,
		typingSpeed,
	}) => {
		const saveDataPayload = {
			answerText,
			questionId,
			quizId,
			finish,
			isMCQ: false,
			isChat: true,
			timeSpentOnAnswer,
			typingSpeed,
		}
		inIframe && GC.sendGameDataSaveMessage(saveDataPayload)
	}

	/**
	 * Submit the question. If all chats are resolved, then end the quiz. 
	 */
	const onEndChat = (id) => {
		const messages = allMessages[id]
		let typingSpeedInWordsPerMin = (chatMetricsRef.current?.[id]?.totalWordsTyped / chatMetricsRef.current?.[id]?.totalTypingTime) * 60
		let replyTimeInSec = (chatMetricsRef.current?.[id]?.totalReplyTime / chatMetricsRef.current?.[id]?.userMsgsCount)
		if (!typingSpeedInWordsPerMin) {
			typingSpeedInWordsPerMin = 0
		}
		if (!replyTimeInSec) {
			replyTimeInSec = timer - timeLeft
		}
		optionSubmitHandler({
			answerText: JSON.stringify(messages),
			finish: true,
			questionId: id,
			timeSpentOnAnswer: replyTimeInSec,
			typingSpeed: typingSpeedInWordsPerMin
		})
		const updatedResolvedQuestions = [...resolvedQuestionsRef.current, id]
		resolvedQuestionsRef.current = updatedResolvedQuestions
		setResolvedQuestions(updatedResolvedQuestions)
		setSubmissionStatus(prev => ({ ...prev, [id]: "submitting" }))
		if (updatedResolvedQuestions.length === Object.keys(allMessagesRef.current)?.length) {
			clearInterval(timerInterval)
			if (!isSubmissionStatusVisible) {
				setIsSubmissionStatusVisible(true)
			}
		}
	}

	/**
	 * Submit all questions except the ones that are already resolved. 
	 */
	const onEndSession = () => {
		const allQuestionIds = Object.keys(allMessagesRef.current)
		allQuestionIds.forEach(questionId => {
			if (!resolvedQuestionsRef.current.includes(questionId)) {
				onEndChat(questionId)
			}
		})
		setIsSubmissionStatusVisible(true)
	}

	/**
	 * Start the quiz timer after instructions.
	 */
	const handleStartChat = () => {
		setInstructionsVisible(false)
		timerInterval = setInterval(() => {
			setTimeLeft(time => time - 1)
		}, 1000)
	}

	/**
	 * Check if there are unread notifications from tickets other than current
	 */
	const hasUnreadNotifications = () => {
		return Object.keys(notificationMessages).some(questionId =>
			questionId !== currentQuestion?.id && notificationMessages[questionId]
		);
	}

	/**
	 * Toggle mobile drawer for tickets
	 */
	const toggleMobileDrawer = () => {
		setMobileDrawerOpen(prev => !prev)
	}

	/**
	 * Ticket Component
	 */
	const TicketItem = ({ questionId, idx, onClick, isMobile = false }) => (
		<article
			key={questionId}
			className={classNames("flex items-center justify-between h-[80px] w-full px-[15px] cursor-pointer", {
				"bg-[#fff]": currentQuestion.id !== questionId,
				"bg-[#F7F8FC]": currentQuestion.id === questionId,
				"border-b border-gray-100": isMobile,
			})}
			onClick={onClick}
		>
			<div className="flex items-center">
				<img src={require("assets/images/Grogu.png")} alt="" className={`m${rtlView ? "l" : "r"}-[15px]`} />
				<div className="flex flex-col items-start">
					<p className={classNames("text-[16px] font-[600]")}>
						Ticket {idx + 1}
					</p>
					<p
						className={classNames("text-[14px] transition-all", {
							"opacity-60 font-[400]": !notificationMessages?.[questionId],
							"font-[500]": !!notificationMessages?.[questionId] && !loadingMessages?.includes(questionId)
						})}
					>
						{
							resolvedQuestions?.includes(questionId)
								? <span className="flex items-center">
									<ChatSlash />
									<span className="ml-1">{t("chat.chat_ended")}</span>
								</span>
								: loadingMessages?.includes(questionId)
									? "Typing..."
									: notificationMessages[questionId]
										? notificationMessages[questionId]?.length > 24
											? `${notificationMessages[questionId]?.slice(0, 24)}...`
											: notificationMessages[questionId]
										: `${allMessages[questionId][allMessages[questionId]?.length - 1]?.content?.slice(0, 24) ?? ""}...`
						}
					</p>
				</div>
			</div>
			{notificationMessages?.[questionId] && (
				<div className="flex flex-col items-center transition-all">
					<p className="text-[12px] capitalize font-[400] opacity-60 mb-[6px]">now</p>
					<div className="bg-[#FF5555] h-[18px] w-[18px] text-[#fff] text-[12px] rounded-full flex items-center justify-center">
						1
					</div>
				</div>
			)}
		</article>
	)

	return currentQuestion ? (
		<div className={classNames("h-full", {
			"bg-white sm:bg-white px-0 py-4 sm:px-0 sm:py-0": instructionsVisible,
			"bg-[#000] pt-0 sm:bg-white": !instructionsVisible
		})}>
			<div dir={direction} className={classNames("relative flex multi-chat-container h-full sm:rounded-lg justify-between gap-10", {
				"bg-white sm:bg-white px-4 py-4 sm:px-0 sm:py-0": instructionsVisible,
				"bg-[#000] pt-0 sm:bg-white": !instructionsVisible
			})}>
				<div className={classNames("absolute left-[-500px] w-[800px] h-[250px] rounded-[800px] bg-[#ac95cf] blur-[256px] sm:hidden", {
					"hidden": instructionsVisible
				})} />
				{
					instructionsVisible ?
						<div className="flex flex-col bg-white h-full rounded-[12px]">
							<div className="sm:block hidden">
								<ChatHeader
									totalTime={timer}
									timeLeft={timeLeft}
									chatStarted={true}
									multiChatQuiz={true}
								>
									<StartChatBtn text={t("chat.start_session")} handleStartChat={handleStartChat} />
								</ChatHeader>
							</div>
							<div className="h-full overflow-y-auto">
								<ChatInstructions
									chatStarted={false}
									question={questions?.[0]?.question}
									totalTime={timer}
									handleStartChat={handleStartChat}
									multiChatQuiz={true}
								/>
							</div>
						</div>
						: <>
							{/* Desktop Layout */}
							<div className="hidden sm:flex h-full w-full">
								<div className="flex h-full w-full flex-col justify-between bg-white rounded-[12px] overflow-hidden">
									<ChatHeader
										totalTime={timer}
										timeLeft={timeLeft}
										chatStarted={true}
										multiChatQuiz={true}
									>
										{Object.keys(allMessages).length === questions.length && (
											<motion.div
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												transition={{ duration: 0.4 }}
												className="flex-1 text-right"
											>
												<EndChatButton
													allowSubmitChat={true}
													submitCloseChat={onEndSession}
													text={t("chat.end_session")}
												/>
											</motion.div>
										)}
									</ChatHeader>
									<motion.div
										dir={direction}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{ duration: 0.4 }}
										className="h-full grid grid-cols-[0.5fr_1.2fr_0.7fr]"
									>
										{/* Desktop Tickets sidebar*/}
										<section className={classNames("flex flex-col w-full h-[81vh] overflow-y-auto", {
											"border-r-solid border-r-[1px] border-r-[#D9D9D9]": !rtlView,
											"border-l-solid border-l-[1px] border-l-[#D9D9D9]": rtlView,
										})}>
											{Object.keys(allMessages ?? [])?.map((questionId, idx) => (
												<TicketItem
													key={questionId}
													questionId={questionId}
													idx={idx}
													onClick={(e) => onQuestionChange(e, questionId)}
												/>
											))}
										</section>
										{/* Chat area */}
										<div className={classNames("h-[81vh] overflow-y-auto flex flex-col justify-end items-stretch relative", {
											"border-r-solid border-r-[1px] border-r-[#D9D9D9]": !rtlView,
											"border-l-solid border-l-[1px] border-l-[#D9D9D9]": rtlView,
										})}>
											<div className={classNames("relative w-full bg-white flex h-full justify-between")}>
												{!resolvedQuestions?.includes(currentQuestion.id) && (
													<div className={classNames("absolute top-[-2px] left-[1px] w-[calc(100%-2px)] flex items-center justify-end bg-[#F7F8FC] border-b-solid border-b-[1px] border-b-[#D9D9D9]")}>
														<EndChatButton
															allowSubmitChat={true}
															submitCloseChat={() => {
																setTimeout(() => {
																	onEndChat(currentQuestion.id)
																}, 200)
															}}
															text={t("chat.resolve_ticket")}
														/>
													</div>
												)}
												<div className={`flex h-full flex-1 flex-col justify-between bg-white w-full overflow-x-hidden px-[15px] scroll-container`}>
													<div
														id="chat-scroll-section"
														className={classNames(
															"flex flex-col gap-6 overflow-y-auto px-2 pt-20 pb-6 h-full"
														)}
													>
														{allMessages?.[currentQuestion?.id]?.map((msg, index) => (
															<MessageBubble
																key={index}
																data={msg}
																wrapperClassName={
																	index === 0 && allMessages?.[currentQuestion?.id]?.length - 1 === index ? "mt-auto mb-4" :
																		index === 0 ? "mt-auto" :
																			allMessages?.[currentQuestion?.id]?.length - 1 == index ? "mb-0" :
																				null
																}
															/>
														))}
														{loadingMessages.includes(currentQuestion?.id) && (
															<TypingLoader
																className={classNames("mb-0", {
																	"mt-auto": !allMessages?.[currentQuestion?.id] || allMessages?.[currentQuestion?.id]?.length === 0,
																})}
															/>
														)}
													</div>
													<div className="mt-0">
														{resolvedQuestions?.includes(currentQuestion.id) ? (
															<motion.div
																initial={{ opacity: 0, y: "20px" }}
																animate={{ opacity: 1, y: "0px" }}
																transition={{ duration: 0.4 }}
															>
																<p className="rounded-[10px] bg-[#4528120A] px-5 py-[22px] opacity-50 text-[#000] text-center font-semibold">
																	{t("chat.chat_end_message")}
																</p>
															</motion.div>
														) : (
															<>
																<ChatTimeWarningProgress timeLeft={timeLeft} />
																<ChatInput
																	ref={inputRef}
																	multiChatQuiz={true}
																	id={currentQuestion.id}
																	getBotReply={getBotReply}
																	setMessages={updateMessages}
																	onTypingStart={onTypingStart}
																	onTypingEnd={onTypingEnd}
																	messages={allMessages?.[currentQuestion.id] ?? []}
																	loadingReply={loadingMessages.includes(currentQuestion?.id)}
																/>
															</>
														)}
													</div>
												</div>
											</div>
										</div>
										{/* Desktop Instructions sidebar */}
										<div className="h-[81vh] overflow-y-auto px-[15px] pt-[15px] transition-all">
											<p className="text-black text-[14px] opacity-50 uppercase mb-5">{t("chat.heading_instructions")} :</p>
											<motion.div
												initial={{ opacity: 0, x: "0px" }}
												animate={{ opacity: 1, x: "0px" }}
												transition={{ duration: 0.4 }}
											>
												<div dangerouslySetInnerHTML={{ __html: currentQuestion?.question }}></div>
											</motion.div>
										</div>
									</motion.div>
								</div>
							</div>

							{/* Mobile Layout */}
							<div className="flex sm:hidden h-full flex-1 flex-col justify-between bg-[#000] scroll-container">
								{/* Chat messages wrapper */}
								<div
									id="chat-scroll-section"
									className={classNames(
										"flex flex-col gap-6 overflow-y-auto sm:h-full px-2 pb-6"
									)}
								>
									<MobileHeader
										chatStarted={true}
										endChatBtn={!resolvedQuestions?.includes(currentQuestion.id) ? (
											<EndChatButton
												allowSubmitChat={true}
												submitCloseChat={() => {
													setTimeout(() => {
														onEndChat(currentQuestion.id)
													}, 200)
												}}
												text={t("chat.resolve_ticket")}
											/>
										) : null}
										openMobileInstructions={() => setMobileInstructionsVisible(true)}
										totalTime={timer}
										timeLeft={timeLeft}
										toggleMobileDrawer={toggleMobileDrawer}
										hasUnreadNotifications={hasUnreadNotifications()}
										multiChatQuiz={true}
									/>
									{allMessages?.[currentQuestion?.id]?.map((msg, index) => (
										<MessageBubble
											key={index}
											data={msg}
											wrapperClassName={
												index === 0 && allMessages?.[currentQuestion?.id]?.length - 1 === index ? "mt-[80px] mb-4" :
													index === 0 ? "mt-[80px]" :
														allMessages?.[currentQuestion?.id]?.length - 1 == index ? "mb-4" :
															null
											}
										/>
									))}
									{loadingMessages.includes(currentQuestion?.id) && (
										<TypingLoader
											className={classNames("mb-6", {
												"mt-auto": !allMessages?.[currentQuestion?.id] || allMessages?.[currentQuestion?.id]?.length === 0,
											})}
										/>
									)}
								</div>
								<div className={classNames(
									"fixed bottom-[1px] mt-20 w-full px-2 py-2"
								)}>
									{resolvedQuestions?.includes(currentQuestion.id) ? (
										<motion.div
											initial={{ opacity: 0, y: "20px" }}
											animate={{ opacity: 1, y: "0px" }}
											transition={{ duration: 0.4 }}
										>
											<p className="rounded-[10px] bg-[#4528120A] px-5 py-[22px] opacity-50 text-[#000] text-center font-semibold">
												{t("chat.chat_end_message")}
											</p>
										</motion.div>
									) : (
										<>
											<ChatTimeWarningProgress timeLeft={timeLeft} />
											<ChatInput
												ref={inputRef}
												multiChatQuiz={true}
												id={currentQuestion.id}
												getBotReply={getBotReply}
												setMessages={updateMessages}
												onTypingStart={onTypingStart}
												onTypingEnd={onTypingEnd}
												messages={allMessages?.[currentQuestion.id] ?? []}
												loadingReply={loadingMessages.includes(currentQuestion?.id)}
											/>
										</>
									)}
								</div>
							</div>

							{/* Mobile Drawer for Tickets */}
							<motion.div
								initial={{ x: "-100%" }}
								animate={{ x: mobileDrawerOpen ? "0%" : "-100%" }}
								transition={{ duration: 0.3, ease: "easeInOut" }}
								className="fixed top-0 left-0 w-[80vw] h-full bg-white mobile-drawer mobile-drawer-shadow sm:hidden"
							>
								<div className="flex flex-col h-full">
									<div className="flex items-center justify-between p-4 border-b border-gray-200">
										<h2 className="text-lg font-semibold">Tickets</h2>
										{Object.keys(allMessages).length === questions.length && (
											<EndChatButton
												allowSubmitChat={true}
												submitCloseChat={onEndSession}
												text={t("chat.end_session")}
											/>
										)}
										<button onClick={toggleMobileDrawer} className="p-2">
											<span className="text-2xl">×</span>
										</button>
									</div>
									<div className="flex-1 overflow-y-auto">
										{Object.keys(allMessages ?? [])?.map((questionId, idx) => (
											<TicketItem
												key={questionId}
												questionId={questionId}
												idx={idx}
												onClick={(e) => {
													onQuestionChange(e, questionId)
													setMobileDrawerOpen(false)
												}}
												isMobile={true}
											/>
										))}
									</div>
								</div>
							</motion.div>

							{/* Mobile Drawer Overlay */}
							{mobileDrawerOpen && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									className="fixed inset-0 bg-black/50 mobile-drawer-overlay sm:hidden"
									onClick={toggleMobileDrawer}
								/>
							)}
						</>
				}
				<MobileChatInstructions
					mobileInstructionsVisible={mobileInstructionsVisible}
					question={currentQuestion?.question}
					closeMobileInstructions={() => setMobileInstructionsVisible(false)}
				/>
			</div>
			<SubmissionStatusModal
				visible={isSubmissionStatusVisible}
				continueHandler={quizEndHandler}
				submissionStatus={submissionStatus}
				allMessages={allMessages}
			/>
		</div>
	) : (<></>)
}

export default MultiChatQuiz